// =================== REPORT / BLOCK MENU ===================
// Reusable moderation menu used across the app. Renders a small kebab (•••)
// button that opens a menu with Report + Block options. Handles the confirm
// dialogs and the API calls. Consumers just tell us which content to target.
//
// Usage:
//   <ReportBlockMenu
//     targetType="post"          // "user" | "post" | "comment" | "message" | "discover_card"
//     targetId={post.id}
//     targetUserId={post.user_id}
//     targetLabel={post.user?.display_name}
//     onBlocked={() => refetch()}  // optional callback after successful block
//     onReported={() => {}}         // optional
//   />
//
import React, { useState } from 'react';
import axios from 'axios';
import { MoreVertical, Flag, Ban, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'harassment',      label: 'Harassment or bullying' },
  { id: 'hate_speech',     label: 'Hate speech' },
  { id: 'sexual_content',  label: 'Sexual or nude content' },
  { id: 'violence',        label: 'Violence or threats' },
  { id: 'impersonation',   label: 'Impersonation' },
  { id: 'spam',            label: 'Spam or scam' },
  { id: 'self_harm',       label: 'Self-harm or dangerous behavior' },
  { id: 'other',           label: 'Something else' },
];

export const ReportBlockMenu = ({
  targetType,
  targetId,
  targetUserId,
  targetLabel,
  canBlock = true,
  onBlocked,
  onReported,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [category, setCategory] = useState('harassment');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReport = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/moderation/report`, {
        target_type: targetType,
        target_id: targetId,
        category,
        description,
      });
      toast.success("Report submitted. Thank you — we'll review within 24 hours.");
      setShowReport(false);
      setDescription('');
      setCategory('harassment');
      onReported && onReported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBlock = async () => {
    if (!targetUserId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/moderation/block/${targetUserId}`);
      toast.success(`${targetLabel || 'User'} blocked. They can no longer contact you.`);
      setShowBlock(false);
      onBlocked && onBlocked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          className="p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground transition-colors"
          data-testid={`moderation-menu-${targetType}-${targetId}`}
          title="Report or block"
          aria-label="Report or block"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-card border border-border rounded-lg shadow-lg py-1"
            data-testid={`moderation-dropdown-${targetType}-${targetId}`}
          >
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); setShowReport(true); }}
              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-muted/50"
              data-testid={`moderation-report-btn-${targetType}-${targetId}`}
            >
              <Flag className="w-4 h-4 text-amber-500" />
              Report {targetType === 'user' ? 'user' : targetType}
            </button>
            {canBlock && targetUserId && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setOpen(false); setShowBlock(true); }}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-muted/50 text-destructive"
                data-testid={`moderation-block-btn-${targetType}-${targetId}`}
              >
                <Ban className="w-4 h-4" />
                Block {targetLabel || 'user'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Report dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-500" /> Report {targetType}
            </DialogTitle>
            <DialogDescription>
              Help us keep The Club Golf safe. Every report is reviewed within 24 hours. False or malicious reports may result in action against your own account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Reason</Label>
              <RadioGroup value={category} onValueChange={setCategory} className="space-y-1">
                {CATEGORIES.map(c => (
                  <div key={c.id} className="flex items-center gap-2">
                    <RadioGroupItem value={c.id} id={`cat-${c.id}`} data-testid={`report-category-${c.id}`} />
                    <Label htmlFor={`cat-${c.id}`} className="text-sm font-normal cursor-pointer">
                      {c.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground mb-2 block">Details (optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
                placeholder="What did you see? Any additional context helps our review."
                rows={3}
                className="resize-none"
                data-testid="report-description-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">{description.length}/2000</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowReport(false)} data-testid="report-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitReport}
              disabled={submitting || !category}
              className="gap-1"
              data-testid="report-submit-btn"
            >
              <Flag className="w-4 h-4" />
              {submitting ? 'Sending...' : 'Submit report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block confirmation dialog */}
      <Dialog open={showBlock} onOpenChange={setShowBlock}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-destructive" /> Block {targetLabel || 'user'}?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">When you block someone:</span>
              <span className="block">• They can&apos;t see your posts or profile</span>
              <span className="block">• You won&apos;t see theirs</span>
              <span className="block">• Any existing match or DM thread is silently closed</span>
              <span className="block">• They aren&apos;t notified</span>
              <span className="block pt-2 text-foreground">You can unblock anytime from Settings.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowBlock(false)} data-testid="block-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitBlock}
              disabled={submitting}
              variant="destructive"
              className="gap-1"
              data-testid="block-confirm-btn"
            >
              <Ban className="w-4 h-4" />
              {submitting ? 'Blocking...' : 'Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportBlockMenu;
